import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Empty } from 'antd';
import { fetchProductsByBrand } from '../../Redux/slice/productSlice';
import { fetchBrands } from '../../Redux/slice/brandSlice';
import RelatedBrands from './RelatedBrands';

const Brand = () => {
  const { brandId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { brandProducts, loading } = useSelector((state) => state.products);
  const { brands } = useSelector((state) => state.brands);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [imageLoadStates, setImageLoadStates] = useState({});
  const observer = useRef();

  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrentPage(1);
    dispatch(fetchBrands());
    dispatch(fetchProductsByBrand(brandId));
  }, [dispatch, brandId]);

  const selectedBrand = brands.find((brand) => brand.brandId === brandId);
  const filteredBrands = selectedBrand
    ? brands.filter((b) => b.categoryId === selectedBrand.categoryId)
    : [];

  const filteredProducts = (brandProducts || [])
    .filter(
      (product) =>
        product.price >= 0 &&
        product.price <= 200000 &&
        product.productID !== '9d88a301-e4ff-42a2-957a-9c611d4cce12'
    )
    .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

  const currentProducts = filteredProducts.slice(0, currentPage * itemsPerPage);

  const calculatePricing = (product) => {
  const currentPrice = product.price;
  const oldPrice = product.oldPrice;

  if (!oldPrice || oldPrice <= currentPrice) return null;

  const discountPercentage = Math.round(((oldPrice - currentPrice) / oldPrice) * 100);

  return {
    currentPrice,
    oldPrice,
    discountPercentage
  };
};


  const handleImageLoad = (id) =>
    setImageLoadStates((prev) => ({ ...prev, [id]: true }));

  const handleImageError = (id) =>
    setImageLoadStates((prev) => ({ ...prev, [id]: 'error' }));

  const lastProductRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setCurrentPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading]
  );

  const brandName = selectedBrand?.brandName || 'Featured Brands';
  const pageTitle = selectedBrand
    ? `Shop ${brandName} Products Online | Franko Trading`
    : 'Explore Branded Products | Franko Trading';
  const description = selectedBrand
    ? `Buy genuine ${brandName} electronics and accessories at Franko Trading. Fast delivery and best prices guaranteed.`
    : 'Browse a wide selection of authentic electronics and accessories from top brands.';
  const imageUrl = `${window.location.origin}/assets/frankoIcon.png`;
  const pageUrl = `https://www.frankotrading.com/brand/${brandId}`;

  return (
    <div className="container mx-auto p-4 md:p-6">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={pageUrl} />
      </Helmet>

      <h3 className="text-md font-semibold mb-4 text-red-500">
        {selectedBrand ? `${brandName} Products` : 'Brand Products'}
      </h3>

      <RelatedBrands
        filteredBrands={filteredBrands}
        selectedBrandId={brandId}
        navigate={navigate}
      />

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 12 }).map((_, index) => (
            <div
              key={index}
              className="animate-pulse border rounded-lg shadow p-3 bg-gray-100 relative"
            >
              <div className="h-40 bg-gray-200 rounded-lg mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4 mb-1"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : currentProducts.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {currentProducts.map((product, index) => {
            const pricing = calculatePricing(product);
            const imageSrc = `https://smfteapi.salesmate.app/Media/Products_Images/${product.productImage?.split('\\').pop()}`;

            return (
              <div
                key={product.productID}
                ref={
                  index === currentProducts.length - 1 ? lastProductRef : null
                }
                className="p-4 bg-white border border-gray-200 rounded-lg shadow-md hover:shadow-xl transition-transform transform hover:scale-105 cursor-pointer relative"
                onClick={() => navigate(`/product/${product.productID}`)}
              >
                {pricing && (
                  <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full z-10">
                    -{pricing.discountPercentage}%
                  </div>
                )}

                <div className="h-40 md:h-40 lg:h-72 w-full flex items-center justify-center mb-3 overflow-hidden rounded-lg ">
                  {!product.productImage ||
                  imageLoadStates[product.productID] === 'error' ? (
                    <div className="animate-pulse w-full h-full  rounded-lg" />
                  ) : (
                    <>
                      {!imageLoadStates[product.productID] && (
                        <div className="animate-pulse w-full h-full  rounded-lg" />
                      )}
                      <img
                        src={imageSrc}
                        alt={product.productName}
                        className={`w-72 h-full object-cover rounded-lg ${
                          !imageLoadStates[product.productID] ? 'hidden' : ''
                        }`}
                        onLoad={() => handleImageLoad(product.productID)}
                        onError={() => handleImageError(product.productID)}
                      />
                    </>
                  )}
                </div>

                <h4 className="text-sm font-medium truncate mb-2">
                  {product.productName}
                </h4>

                <div className="flex flex-col space-y-1">
                  <span className="text-red-500 font-semibold text-sm">
                    ₵{product.price.toLocaleString()}.00
                  </span>
                  {pricing?.oldPrice && (
                    <span className="text-gray-400 line-through text-xs">
                      ₵{pricing.oldPrice.toLocaleString()}.00
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Empty description="No products available" />
      )}
    </div>
  );
};

export default Brand;
